import React, { createContext, ReactNode, useContext, useMemo } from 'react';

import { useSinglePriceSettings } from './use-single-price-settings';

interface PriceSettingsContextValue {
  minimumFractionDigits: number;
  maximumFractionDigits: number;
  maximum: number;
  minimum: number;
}

interface UseSinglePriceSettingsProps {
  maximumFractionDigits?: number;
  minimumFractionDigits?: number;
}

// Create the context
const PriceSettingsContext = createContext<PriceSettingsContextValue | null>(
  null
);

// Provider component
interface PriceSettingsProviderProps {
  children: ReactNode;
}

export const PriceSettingsProvider: React.FC<PriceSettingsProviderProps> = ({
  children,
}) => {
  const priceSettings = useSinglePriceSettings();

  return (
    <PriceSettingsContext.Provider value={priceSettings}>
      {children}
    </PriceSettingsContext.Provider>
  );
};

// Hook to use the context with fallback to props
export const useSinglePriceWithContext = (
  props?: UseSinglePriceSettingsProps
) => {
  const contextValue = useContext(PriceSettingsContext);

  // Use context if available, otherwise fall back to props or default values
  return useMemo(() => {
    if (contextValue) {
      return contextValue;
    }

    // Fallback to props if provided, otherwise use default values
    const minimumFractionDigits = props?.minimumFractionDigits ?? 2;
    const maximumFractionDigits = props?.maximumFractionDigits ?? 2;

    return {
      minimumFractionDigits,
      maximumFractionDigits,
      maximum: maximumFractionDigits,
      minimum: minimumFractionDigits,
    };
  }, [
    contextValue,
    props?.minimumFractionDigits,
    props?.maximumFractionDigits,
  ]);
};
